import React, { useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import { uploadDocument, updateDocument, destroyDocument } from "../api/library"
import { dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const CATEGORIES = ["contract", "specification", "plan", "administrative", "other"]

const ICON_FOR_CONTENT_TYPE = (ct) => {
  if (!ct) return "file"
  if (ct.startsWith("image/"))                                 return "file-image"
  if (ct === "application/pdf")                                return "file-pdf"
  if (ct.includes("word") || ct === "application/msword")      return "file-word"
  if (ct.includes("excel") || ct.includes("spreadsheet"))      return "file-excel"
  if (ct.includes("zip") || ct.includes("compressed"))         return "file-zipper"
  if (ct.startsWith("text/"))                                  return "file-lines"
  return "file"
}

const formatBytes = (bytes) => {
  if (!bytes) return "—"
  const units = [
    I18n.t("projects.library.size_units.b"),
    I18n.t("projects.library.size_units.kb"),
    I18n.t("projects.library.size_units.mb"),
    I18n.t("projects.library.size_units.gb"),
  ]
  let value   = Number(bytes)
  let i       = 0
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1 }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`
}

const CategoryColumn = ({ category, items, canManage, fileInputRef, onSetPendingCategory, onChangeCategory, onRename, onDelete }) => (
  <div className="bg-white border border-slate-200 rounded-xl flex flex-col">
    <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between">
      <div>
        <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">
          {I18n.t(`projects.library.categories.${category}`)}
        </div>
        <div className="text-[11.5px] text-slate-500 mt-0.5">
          {I18n.t("projects.library.count", { count: items.length })}
        </div>
      </div>
      {canManage && (
        <button
          type      = "button"
          onClick   = {() => { onSetPendingCategory(category); fileInputRef.current?.click() }}
          className = "px-2 py-1 rounded text-[11.5px] text-blue-600 hover:bg-blue-50 inline-flex items-center gap-1"
        >
          <FontAwesomeIcon icon="upload" className="text-[11px]" /> {I18n.t("projects.library.add")}
        </button>
      )}
    </div>
    <ul className="flex-1 divide-y divide-slate-100">
      {items.length === 0 ? (
        <li className="px-4 py-6 text-[12.5px] text-slate-400 text-center">
          {I18n.t("projects.library.empty_category")}
        </li>
      ) : items.map(doc => (
        <li key={doc.id} className="px-4 py-2.5 flex items-center gap-2.5 group hover:bg-slate-50/60">
          <FontAwesomeIcon icon={ICON_FOR_CONTENT_TYPE(doc.content_type)} className="w-4 h-4 text-slate-400 shrink-0" />
          <a href={doc.download_url} download className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="text-[12.5px] font-medium text-slate-900 truncate group-hover:text-blue-700" title={doc.name}>
              {doc.name}
            </span>
            <span className="text-[10.5px] text-slate-500 flex items-baseline gap-x-2 min-w-0">
              <span className="whitespace-nowrap tabular-nums">{formatBytes(doc.file_size_bytes)}</span>
              {doc.created_at && <span className="whitespace-nowrap">· {dateFmt(doc.created_at)}</span>}
            </span>
          </a>
          {canManage && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <IconButton size="sm" title={I18n.t("projects.library.rename")} onClick={() => onRename(doc)}>
                <FontAwesomeIcon icon="pen" />
              </IconButton>
              <select
                value     = {doc.category}
                onChange  = {e => onChangeCategory(doc, e.target.value)}
                onClick   = {e => e.stopPropagation()}
                title     = {I18n.t("projects.library.move_to")}
                className = "h-6 text-[10.5px] px-1 border border-slate-200 rounded bg-white"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{I18n.t(`projects.library.categories.${c}`)}</option>
                ))}
              </select>
              <IconButton tone="danger" size="sm" title={I18n.t("projects.library.destroy")} onClick={() => onDelete(doc)}>
                <FontAwesomeIcon icon="trash" />
              </IconButton>
            </div>
          )}
        </li>
      ))}
    </ul>
  </div>
)

const LibrarySection = ({ library: initial, policies, title, subtitle, notice, padded = true }) => {
  const baseUrl   = initial?.create_url
  const canManage = !!(policies ? policies.can_manage_library : initial?.can_manage)
  const fileInputRef    = useRef(null)
  const pendingCategory = useRef("other")
  const [items,  setItems] = useState(initial?.items || [])
  const [uploading, setUploading] = useState(false)
  const [errors,  setErrors]      = useState([])

  const grouped = useMemo(() => {
    const result = {}
    CATEGORIES.forEach(c => result[c] = [])
    items.forEach(doc => {
      const c = CATEGORIES.includes(doc.category) ? doc.category : "other"
      result[c].push(doc)
    })
    return result
  }, [items])

  const upsert = (record) => {
    setItems(prev => prev.some(it => it.id === record.id)
      ? prev.map(it => it.id === record.id ? record : it)
      : [record, ...prev])
  }

  const handleFiles = (files) => {
    if (!files || files.length === 0) return
    setUploading(true)
    setErrors([])
    const uploads = Array.from(files).map(file =>
      uploadDocument(baseUrl, file, { category: pendingCategory.current })
        .then(response => {
          if (response?.errors) return { error: response.errors }
          upsert(response)
          return { ok: true }
        })
    )
    Promise.all(uploads).then(results => {
      const failed = results.filter(r => r.error)
      if (failed.length > 0) setErrors([failed[0].error].flat())
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
      pendingCategory.current = "other"
    }).catch(() => {
      setErrors([I18n.t("projects.library.errors.network")])
      setUploading(false)
    })
  }

  const handleRename = (doc) => {
    const next = prompt(I18n.t("projects.library.rename_prompt"), doc.name)
    if (next == null || next.trim() === "" || next === doc.name) return
    updateDocument(baseUrl, doc.id, { name: next.trim() }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      upsert(response)
    })
  }

  const handleChangeCategory = (doc, category) => {
    updateDocument(baseUrl, doc.id, { category }).then(response => {
      if (response?.errors) { flashAlert(response.errors.join(", ")); return }
      upsert(response)
    })
  }

  const handleDelete = (doc) => {
    if (!confirm(I18n.t("projects.library.confirm_destroy", { name: doc.name }))) return
    destroyDocument(baseUrl, doc.id).then(response => {
      if (response?.success) setItems(prev => prev.filter(it => it.id !== doc.id))
    })
  }

  const totalBytes = items.reduce((acc, it) => acc + (it.file_size_bytes || 0), 0)

  return (
    <section id="section-library" className={`${padded ? "px-7 " : ""}scroll-mt-24`}>
      <header className="pl-[16px] mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{title || I18n.t("projects.library.title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {subtitle || I18n.t("projects.library.subtitle")}
            {items.length > 0 && (
              <>
                {" — "}
                <span className="font-semibold text-slate-900">{I18n.t("projects.library.count", { count: items.length })}</span>
                <span className="text-slate-400"> · {formatBytes(totalBytes)}</span>
              </>
            )}
          </p>
        </div>
        {canManage && (
          <ActionButton
            icon     = {uploading ? "spinner" : "upload"}
            disabled = {uploading}
            onClick  = {() => { pendingCategory.current = "other"; fileInputRef.current?.click() }}
          >
            {I18n.t("projects.library.upload")}
          </ActionButton>
        )}
        <input
          ref      = {fileInputRef}
          type     = "file"
          multiple
          onChange = {e => handleFiles(e.target.files)}
          className= "hidden"
        />
      </header>

      {notice}

      {errors.length > 0 && (
        <div className="mb-3 px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {errors.join(", ")}
        </div>
      )}

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {CATEGORIES.map(category => (
          <CategoryColumn
            key                  = {category}
            category             = {category}
            items                = {grouped[category]}
            canManage            = {canManage}
            fileInputRef         = {fileInputRef}
            onSetPendingCategory = {(c) => { pendingCategory.current = c }}
            onChangeCategory     = {handleChangeCategory}
            onRename             = {handleRename}
            onDelete             = {handleDelete}
          />
        ))}
      </div>
    </section>
  )
}

export default LibrarySection
